/**
 * 필터 필드 id — 그대로 폼 값 키가 된다.
 *
 * 옛 폼 값(`InboundInquiryFormValues`)의 키 — 엔트리(index.tsx)가 컨덕터 뷰의 조회영역 값을 이 키로 옮겨
 * `filterToRequest.ts`·`searchQuery.ts` 를 그대로 잇는다. `serviceTypes`는 배열, BE 계약상 `shipperCode`는 단일값이지만
 * 뷰의 화주명 칸이 다중 선택이라 배열로 오고, 2개 이상 고르면 조회 전에 막는다(hasTooManyShipperNames). BE 계약은 `GetInboundsRequest`(3.1 참조).
 */
export const FIELD_NAMES = {
  serviceTypes: 'serviceTypes',
  shipperCode: 'shipperCode',
  plnDateRange: 'plnDateRange',
  ordOptionNumbers: 'ordOptionNumbers',
  orderNumbers: 'orderNumbers',
  orderType: 'orderType',
  actDate: 'actDate',
  warehouseName: 'warehouseName',
} as const

/** 화주유형 옵션 — 핸드오프 데모 `SHIPPER_TYPE_OPTIONS` 6종. */
export const SHIPPER_TYPE_OPTIONS = [
  { value: 'M1P', label: 'M1P' },
  { value: 'MFS', label: 'MFS' },
  { value: 'MWP', label: 'MWP' },
  { value: 'MLT', label: 'MLT' },
  { value: 'MCD', label: 'MCD' },
  { value: 'MTP', label: 'MTP' },
] as const

/**
 * 화주유형 기본 선택값. 통합 운영(MUSINSA) 계정으로 화면을 열 때 미리 골라 둔다 —
 * 화주 고정 계정은 자기 서비스유형이 기본값이라 이 값을 쓰지 않는다(index.tsx `defaultFilterValues`·`filterFallback` 참조).
 */
export const DEFAULT_SERVICE_TYPE = 'M1P'

/**
 * 입고유형 옵션 — moms-be `OrderType.displayName` 실측이 정본이다(R-11).
 *
 * 핸드오프 데모 `ORDTYPESELECT_OPTIONS` 에는 폐기·테스트2·증정·재고이동·반품이 더 있다.
 * 앞 4개는 2.0 이 400 으로 막는 값이라 옵션에서 뺐다(`AllInboundSearchService` 준용).
 * 반품은 400 은 아니지만 서버 고정조건(`ORD_TYPE != '반품'`)에 걸려 항상 0건이라 함께 뺀다.
 * TODO(spec): 해외(현지)·기타·미지정이 핸드오프 데모에 없는 값이다 — 기획 확인 필요(R-11).
 */
export const ORDER_TYPE_OPTIONS = [
  { value: '일반', label: '일반' },
  { value: 'PLUS', label: 'PLUS' },
  { value: '사내', label: '사내' },
  { value: '사내(해외)', label: '사내(해외)' },
  { value: '명품', label: '명품' },
  { value: '해외', label: '해외' },
  { value: '해외(현지)', label: '해외(현지)' },
  { value: '교환', label: '교환' },
  { value: '기타', label: '기타' },
  { value: '미지정', label: '미지정' },
] as const

/** 조회 요청·저장(URL/세션)·뷰 날짜 칸이 함께 쓰는 날짜 포맷 — `GetInboundsReq` 의 `YYYY-MM-DD`(취소 요청의 `yyyyMMdd` 와 다르다). */
export const DATE_FORMAT = 'YYYY-MM-DD'

/** 화주 목록 전건 조회 크기 — 화주 선택지를 한 번에 모두 읽기 위한 상한(GR05 준용). HEAD 조회 상한(1000)과 다른 엔드포인트다. */
export const SHIPPER_LIST_ALL_SIZE = 10000

/** HEAD 엑셀 상한 — BE `InboundSearchService.MAX_EXCEL_ROWS`. ITEM 은 상한이 없다. */
export const EXCEL_ROW_LIMIT = 30_000

/** 입고번호 필터 상한 — BE `ordOptionNumbers` IN 최대 200개. */
export const ORD_OPTION_NUMBER_LIMIT = 200

/**
 * 대표입고번호 필터 상한 — BE `GetInboundsRequest.orderNumbers` 에도 같은
 * `@Size(max = 200)` 이 붙어 있다("주문번호는 최대 200개까지 입력 가능합니다"). 입고번호와 값은
 * 같지만 검증이 따로 걸린 필드라 상수도 따로 둔다.
 */
export const ORDER_NUMBER_LIMIT = 200

/** 상세사유 최소 길이 — 핸드오프 모달 `okValidate.minLength`. */
export const REASON_DETAIL_MIN_LENGTH = 5

/**
 * 상세사유 최대 길이. BE 는 사유를 감사로그 `MOMS_USER_ACCESS_LOG.remark`(varchar 1000)에 넣고,
 * 이 화면은 사유를 쿼리스트링(`detailedReason`)으로 싣는다 — 한글은 URL 인코딩 시 3배로 불어나므로
 * 넉넉히 잡아도 200 이면 충분하다(GR05 준용).
 */
export const REASON_DETAIL_MAX_LENGTH = 200

export const MESSAGES = {
  searchNoRows: '조회된 데이터가 없습니다.',
  requiredFilter: '화주(유형 또는 명)와 입고예정일자는 반드시 입력해야 합니다.',
  /** BE `shipperCode` 는 단일값만 받는다 — 뷰의 화주명 칸이 다중 선택이라 2개 이상 고르면 조회 전에 막는다. */
  tooManyShipperNames: '화주명은 하나만 선택할 수 있습니다.',
  /** 화주유형을 바꾼 뒤 남은 화주명 — 그 조합은 항상 0건이라 조회 전에 막는다(inventoryStatus 선례). */
  shipperNameNotInType: '선택한 화주명이 화주유형과 맞지 않습니다. 화주명을 다시 선택해 주세요.',
  ordOptionNumberLimitExceeded: `입고번호는 최대 ${ORD_OPTION_NUMBER_LIMIT}개까지 조회할 수 있습니다.`,
  orderNumberLimitExceeded: `대표입고번호는 최대 ${ORDER_NUMBER_LIMIT}개까지 조회할 수 있습니다.`,
  excelNoRows: '다운로드할 데이터가 없습니다.',
  excelRowLimitExceeded: `엑셀 다운로드 최대 행수(${EXCEL_ROW_LIMIT.toLocaleString()}행)를 초과했습니다. 조회 조건을 추가해 주세요.`,
  noHeadSelection: '선택된 데이터가 존재하지 않습니다.',
  cancelMissingCutSeq: '차수 정보가 없는 건이 포함되어 있습니다. 해당 건을 선택에서 제외해 주세요.',
  cancelFailed: '취소 처리 중 오류가 발생했습니다.',
  cancelSucceeded: (success: number, fail: number) =>
    fail > 0 ? `입고취소 ${success}건 성공, ${fail}건 실패했습니다.` : `${success}건 입고취소 완료했습니다.`,
  itemMissingCutSeq: '차수 정보가 없어 품목을 조회할 수 없습니다.',
  itemSearchFailed: '품목 조회 중 오류가 발생했습니다.',
  /** [바코드 인쇄] — 상단(noHeadSelection)과 문구를 달리해 어느 표에서 골라야 하는지 알린다(리뷰 2026-09-29). */
  noItemSelection: '인쇄할 품목을 하단 표에서 선택해 주세요.',
  printNoBarcodeRows: '선택한 행에 인쇄 가능한 바코드가 없습니다.',
  printSpecUnsupported: '선택한 규격은 아직 지원하지 않습니다. 다른 규격을 선택해 주세요.',
  printPopupBlocked: '팝업이 차단되어 인쇄 창을 열 수 없습니다. 팝업을 허용한 뒤 다시 시도해 주세요.',
  /** 하단(품목) 엑셀은 BE 가 입고 건 하나(ordOptionNumber·clsDate·cutSeq)만 받는다 — 상단에서 여러 건을 고른 채로는 내려받지 않는다. */
  itemExcelSinglePick: '하단 엑셀은 상단 목록에서 입고 건 하나만 선택했을 때 내려받을 수 있습니다.',
  downloadValidation: `사유코드를 고르고 상세사유를 ${REASON_DETAIL_MIN_LENGTH}글자 이상 입력해 주세요.`,
  downloadTooLong: `상세사유는 ${REASON_DETAIL_MAX_LENGTH}글자까지 입력할 수 있습니다.`,
} as const
